import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { TextSubHeading, IconSecure, colors } from '@blocket/blocket-ui'
import { onEnterKeyEvent } from 'utils/events'
import {
  Modal,
  ModalHeader,
  ModalHeaderText,
  ModalContent,
  ModalContentHeading,
  InfoWrapper,
  InfoText,
  PillWrapper,
  Pill,
  DescriptionForm,
  DescriptionHeader,
  DescriptionHeading,
  DescriptionText,
  DescriptionInfo,
  DescriptionTextArea,
  ThankYouHeading,
  ThankYouText,
  InfoBox,
  InfoBoxTextWrapper,
  InfoBoxText,
  Link,
  CloseButton,
  CloseIcon,
  SubmitButton,
  IconIllustrativeOk,
  IconIllustrativeInfo,
  IconRight,
  GeneralErrorText,
} from './styles.js'

// notice that label isn't equal to the "descReq", we've decided to map them like this at the current state
const defaultOptions = [
  {
    label: 'Illegalt innehåll',
    reportCategory: 'inappropriate',
    trackingCategory: 'inappropriate',
    descriptionRequired: true,
  },
  {
    label: 'Hot/Trakasserier',
    reportCategory: 'harassing',
    trackingCategory: 'harassing',
    descriptionRequired: true,
  },
  {
    label: 'Annat',
    reportCategory: 'suspicious',
    trackingCategory: 'suspicious',
    descriptionRequired: true,
  },
]

const ReportUserModal = ({
  label = 'report user',
  onClose,
  open,
  renderPage = 'form',
  options = defaultOptions,
  onChangeInput,
  onSubmitForm,
  onFocusInput,
  onPillClick,
  formValidation,
  viewTracking = null,
  onLinkClick,
  reportUserPage = 'chat',
  generalError,
  isMobile,
}) => {
  const [activeOption, setActiveOption] = useState()
  const closeModal = onClose
  const okIconSize = isMobile ? 64 : 104
  const page = useRef()

  const thankYouText =
    reportUserPage === 'chat'
      ? 'Användaren är blockerad från att skriva till dig via Meddelanden. Det syns endast för dig.'
      : 'Användaren är nu rapporterad och kundtjänst utreder ärendet.'

  useEffect(() => {
    // only triggers once per view
    if (viewTracking && page.current !== renderPage) {
      page.current = renderPage
      viewTracking()
    }
  }, [viewTracking, renderPage])

  return (
    <Modal open={open} label={label} onClose={onClose}>
      <ModalHeader>
        <TextSubHeading>Rapportera användaren</TextSubHeading>
        {isMobile ? (
          <ModalHeaderText onClick={closeModal}>Stäng</ModalHeaderText>
        ) : (
          <CloseIcon width={32} height={32} onClick={closeModal} />
        )}
      </ModalHeader>
      <ModalContent>
        {renderPage === 'form' && (
          <>
            <ModalContentHeading>Vad vill du rapportera?</ModalContentHeading>
            <InfoWrapper>
              <IconSecure width={24} height={24} />
              <InfoText>Delas inte med den du rapporterar</InfoText>
            </InfoWrapper>
            <PillWrapper>
              {options.map((option) => (
                <Pill
                  key={option.label}
                  tabIndex="0"
                  onClick={() => {
                    onPillClick(option)
                    setActiveOption(option)
                  }}
                  onKeyDown={(e) =>
                    onEnterKeyEvent(e, () => {
                      onPillClick(option)
                      setActiveOption(option)
                    })
                  }
                  active={option.label === activeOption?.label}
                >
                  {option.label}
                </Pill>
              ))}
            </PillWrapper>
            {activeOption && (
              <DescriptionForm onSubmit={onSubmitForm}>
                <DescriptionHeader>
                  <DescriptionHeading>Beskrivning</DescriptionHeading>
                  <DescriptionText>
                    (
                    {activeOption.descriptionRequired || activeOption.requiresFreeText
                      ? 'Obligatoriskt'
                      : 'Valfritt'}
                    )
                  </DescriptionText>
                </DescriptionHeader>
                {reportUserPage === 'chat' && (
                  <DescriptionInfo>Meddelandehistoriken ingår i rapporten.</DescriptionInfo>
                )}
                <DescriptionTextArea
                  name="report-input"
                  type="text"
                  placeholder="Beskriv din rapport"
                  onChange={(e) => onChangeInput(e.target.value)}
                  status={formValidation?.status || null}
                  errorText={formValidation.text}
                  onFocus={onFocusInput}
                />
                <SubmitButton
                  type="primary"
                  htmlType="submit"
                  disabled={formValidation.status === 'error'}
                >
                  Skicka rapport
                </SubmitButton>

                {generalError && (
                  <>
                    <GeneralErrorText> {generalError.message} </GeneralErrorText>
                    <GeneralErrorText> {generalError.errorCode} </GeneralErrorText>
                  </>
                )}
              </DescriptionForm>
            )}
          </>
        )}
        {renderPage === 'sent' && (
          <>
            <IconIllustrativeOk width={okIconSize} height={okIconSize} />
            <ThankYouHeading>Tack för din rapport</ThankYouHeading>
            <ThankYouText>{thankYouText}</ThankYouText>
            <InfoBox>
              <IconIllustrativeInfo width={24} height={24} />
              <InfoBoxTextWrapper>
                <InfoBoxText>
                  Vi ger ingen personlig återkoppling på din rapport. Om du har frågor eller behöver
                  hjälp kan du alltid skriva till kundservice.
                </InfoBoxText>
                <Link
                  href="https://blocket.zendesk.com/hc/sv/requests/new"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onLinkClick}
                >
                  Kontakta kundservice
                  <IconRight width={16} height={16} color={colors.blue600} />
                </Link>
              </InfoBoxTextWrapper>
            </InfoBox>
            <CloseButton onClick={closeModal}>Stäng</CloseButton>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}

ReportUserModal.propTypes = {
  label: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  options: PropTypes.array,
  onChangeInput: PropTypes.func.isRequired,
  onFocusInput: PropTypes.func,
  onSubmitForm: PropTypes.func.isRequired,
  onPillClick: PropTypes.func,
  formValidation: PropTypes.shape({
    status: PropTypes.oneOf(['success', 'error']),
    text: PropTypes.string,
  }),
  renderPage: PropTypes.oneOf(['form', 'sent']),
  viewTracking: PropTypes.func,
  onLinkClick: PropTypes.func,
  reportUserPage: PropTypes.string,
  generalError: PropTypes.object,
  isMobile: PropTypes.bool.isRequired,
}

export default ReportUserModal
